import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getExpiredAccounts, previewCleanup, runCleanup } from '../services/adminApi';

function ExpiredRow({ account, selected, onSelect }) {
  const daysSinceBlocked = account.blockedDate
    ? Math.floor((Date.now() - new Date(account.blockedDate).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // Check if email is actually a UUID (user deleted from Cognito)
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(account.email);
  const displayEmail = isUUID ? null : account.email;
  const isDeletedUser = isUUID || !account.email;

  return (
    <tr className="table-row border-b border-navy-700/50">
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelect(account.id, e.target.checked)}
          className="w-4 h-4 rounded border-navy-500 bg-navy-800 text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
        />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${isDeletedUser ? 'bg-navy-700' : 'bg-coral-600/20'} flex items-center justify-center flex-shrink-0`}>
            {isDeletedUser ? (
              <svg className="w-5 h-5 text-navy-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            ) : (
              <span className="text-sm font-medium text-coral-400">
                {(displayEmail?.[0] || 'U').toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            {isDeletedUser ? (
              <>
                <span className="font-medium text-navy-400 italic">Deleted from Cognito</span>
                <p className="text-xs text-navy-500 font-mono truncate">{account.id}</p>
              </>
            ) : (
              <>
                <Link to={`/users/${account.id}`} className="font-medium text-white hover:text-teal-400 transition-colors truncate block">
                  {displayEmail}
                </Link>
                {account.name && <p className="text-sm text-navy-400 truncate">{account.name}</p>}
              </>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 hidden md:table-cell">
        <span className="text-navy-300">
          {account.blockedDate ? new Date(account.blockedDate).toLocaleDateString() : '-'}
        </span>
      </td>
      <td className="px-6 py-4 hidden lg:table-cell">
        <span className={`text-sm font-medium ${daysSinceBlocked > 75 ? 'text-coral-400' : 'text-navy-300'}`}>
          {daysSinceBlocked} days
        </span>
      </td>
      <td className="px-6 py-4 hidden xl:table-cell">
        <span className="text-navy-400 text-sm font-mono">{account.documentsCount || 0}</span>
      </td>
      <td className="px-6 py-4 hidden xl:table-cell">
        <span className="text-navy-400 text-sm">{account.storageMB?.toFixed(1) || 0} MB</span>
      </td>
      <td className="px-6 py-4 text-right">
        <Link
          to={`/users/${account.id}`}
          className="p-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors inline-block"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </Link>
      </td>
    </tr>
  );
}

const ITEMS_PER_PAGE = 20;

export default function ExpiredAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(accounts.length / ITEMS_PER_PAGE);
  const paginatedAccounts = accounts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getExpiredAccounts({ limit: 500 }); // Load more items
      setAccounts(data.accounts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleSelect = (id, isSelected) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      // Select all on current page
      const currentPageIds = paginatedAccounts.map(a => a.id);
      setSelected(prev => new Set([...prev, ...currentPageIds]));
    } else {
      // Deselect all on current page
      const currentPageIds = new Set(paginatedAccounts.map(a => a.id));
      setSelected(prev => new Set([...prev].filter(id => !currentPageIds.has(id))));
    }
  };
  
  const handleSelectAllPages = () => {
    setSelected(new Set(accounts.map(a => a.id)));
  };
  
  const handleDeselectAll = () => {
    setSelected(new Set());
  };

  const handlePreviewCleanup = async () => {
    try {
      setShowPreview(true);
      const data = await previewCleanup();
      setPreview(data);
    } catch (err) {
      alert('Failed to generate preview: ' + err.message);
      setShowPreview(false);
    }
  };

  const handleRunCleanup = async () => {
    const userIds = Array.from(selected);
    if (userIds.length === 0) {
      alert('Please select accounts to delete');
      return;
    }

    if (!confirm(`Are you sure you want to permanently delete ${userIds.length} account(s)? This action cannot be undone.`)) {
      return;
    }

    setCleaning(true);
    setProgress({ current: 0, total: userIds.length });

    try {
      await runCleanup(userIds);
      setAccounts(prev => prev.filter(a => !selected.has(a.id)));
      setSelected(new Set());
      alert(`Successfully deleted ${userIds.length} account(s)`);
    } catch (err) {
      alert('Cleanup failed: ' + err.message);
    } finally {
      setCleaning(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Expired Accounts</h1>
          <p className="text-navy-300 mt-1">Accounts blocked for more than 60 days</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handlePreviewCleanup} className="btn-secondary flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview Impact
          </button>
          <button
            onClick={handleRunCleanup}
            disabled={selected.size === 0 || cleaning}
            className="btn-danger flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cleaning ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Cleaning...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Selected ({selected.size})
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/80 animate-fade-in">
          <div className="card max-w-lg w-full max-h-[80vh] overflow-y-auto animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Cleanup Impact Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {preview ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-coral-600/10 border border-coral-600/20 rounded-lg">
                    <p className="text-2xl font-bold text-coral-400">{preview.accountsToDelete}</p>
                    <p className="text-sm text-navy-400">Accounts to delete</p>
                  </div>
                  <div className="p-4 bg-coral-600/10 border border-coral-600/20 rounded-lg">
                    <p className="text-2xl font-bold text-coral-400">{preview.documentsToDelete}</p>
                    <p className="text-sm text-navy-400">Documents to delete</p>
                  </div>
                  <div className="p-4 bg-coral-600/10 border border-coral-600/20 rounded-lg">
                    <p className="text-2xl font-bold text-coral-400">{preview.storageToFree?.toFixed(2)} GB</p>
                    <p className="text-sm text-navy-400">Storage to free</p>
                  </div>
                  <div className="p-4 bg-coral-600/10 border border-coral-600/20 rounded-lg">
                    <p className="text-2xl font-bold text-coral-400">{preview.shipbookRecords}</p>
                    <p className="text-sm text-navy-400">ShipBook records</p>
                  </div>
                </div>

                <div className="p-4 bg-navy-800 rounded-lg">
                  <p className="text-sm text-navy-300">
                    This action will permanently delete all user data including profiles, documents, logs, and ShipBook connections for accounts that have been blocked for more than 60 days.
                  </p>
                </div>

                <button
                  onClick={() => {
                    setShowPreview(false);
                    handleSelectAll(true);
                  }}
                  className="w-full btn-primary"
                >
                  Select All for Deletion
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress indicator */}
      {cleaning && progress.total > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-medium">Cleaning up accounts...</span>
            <span className="text-navy-300">{progress.current} / {progress.total}</span>
          </div>
          <div className="h-2 bg-navy-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-coral-500 rounded-full transition-all duration-300"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-4 h-4 loading-shimmer rounded" />
                <div className="w-10 h-10 rounded-full loading-shimmer" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 loading-shimmer rounded" />
                  <div className="h-3 w-1/4 loading-shimmer rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-coral-400">{error}</p>
            <button onClick={loadAccounts} className="btn-primary mt-4">
              Retry
            </button>
          </div>
        ) : accounts.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-white font-medium">No expired accounts</p>
            <p className="text-navy-400 text-sm mt-1">All accounts are within the 60-day grace period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-navy-800/50 border-b border-navy-700">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={paginatedAccounts.length > 0 && paginatedAccounts.every(a => selected.has(a.id))}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-navy-500 bg-navy-800 text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
                      title="Select all on this page"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider hidden md:table-cell">Blocked Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider hidden lg:table-cell">Days Blocked</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider hidden xl:table-cell">Docs</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy-300 uppercase tracking-wider hidden xl:table-cell">Storage</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-navy-300 uppercase tracking-wider">View</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAccounts.map((account) => (
                  <ExpiredRow
                    key={account.id}
                    account={account}
                    selected={selected.has(account.id)}
                    onSelect={handleSelect}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {accounts.length > ITEMS_PER_PAGE && (
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-navy-400 text-sm">
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, accounts.length)} of {accounts.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'bg-teal-600 text-white'
                          : 'text-navy-400 hover:text-white hover:bg-navy-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {accounts.length > 0 && (
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-white font-medium">{accounts.length} expired accounts total</p>
              <p className="text-navy-400 text-sm">
                {selected.size} selected for deletion
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-navy-400">
                Storage: {accounts.reduce((sum, a) => sum + (a.storageMB || 0), 0).toFixed(1)} MB
              </div>
              {selected.size < accounts.length && (
                <button
                  onClick={handleSelectAllPages}
                  className="text-sm text-teal-400 hover:text-teal-300 transition-colors"
                >
                  Select all {accounts.length}
                </button>
              )}
              {selected.size > 0 && (
                <button
                  onClick={handleDeselectAll}
                  className="text-sm text-navy-400 hover:text-white transition-colors"
                >
                  Deselect all
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
